import React from 'react';

/**
 * Minimal SQL syntax highlighter.
 *
 * Deliberately hand-rolled rather than pulling in Prism/highlight.js: the agent
 * sidebar only ever renders Snowflake SQL, and a full highlighter would ship a
 * grammar bundle several times the size of this file for no extra fidelity.
 *
 * `highlightSql` returns React nodes, so callers keep control of the surrounding
 * <pre> (padding, scroll, max-height) and only hand off the colouring.
 */

const KEYWORDS = new Set([
    'ADD', 'ALL', 'ALTER', 'AND', 'ANY', 'AS', 'ASC', 'BETWEEN', 'BY', 'CASE',
    'CAST', 'COLUMN', 'CREATE', 'CROSS', 'CUBE', 'CURRENT', 'DATABASE', 'DELETE',
    'DESC', 'DISTINCT', 'DROP', 'ELSE', 'END', 'EXCEPT', 'EXISTS', 'FALSE',
    'FETCH', 'FILTER', 'FIRST', 'FOLLOWING', 'FROM', 'FULL', 'GROUP', 'GROUPING',
    'HAVING', 'ILIKE', 'IN', 'INNER', 'INSERT', 'INTERSECT', 'INTO', 'IS',
    'JOIN', 'LAST', 'LATERAL', 'LEFT', 'LIKE', 'LIMIT', 'MERGE', 'NATURAL',
    'NOT', 'NULL', 'NULLS', 'OFFSET', 'ON', 'OR', 'ORDER', 'OUTER', 'OVER',
    'PARTITION', 'PIVOT', 'PRECEDING', 'QUALIFY', 'RANGE', 'RECURSIVE', 'RENAME',
    'REPLACE', 'RIGHT', 'ROLLUP', 'ROW', 'ROWS', 'SCHEMA', 'SELECT', 'SET',
    'TABLE', 'THEN', 'TOP', 'TRUE', 'UNBOUNDED', 'UNION', 'UNPIVOT', 'UPDATE',
    'USING', 'VALUES', 'VIEW', 'WHEN', 'WHERE', 'WINDOW', 'WITH',
]);

const TYPES = new Set([
    'ARRAY', 'BIGINT', 'BOOLEAN', 'CHAR', 'DATE', 'DATETIME', 'DECIMAL',
    'DOUBLE', 'FLOAT', 'GEOGRAPHY', 'INT', 'INTEGER', 'NUMBER', 'NUMERIC',
    'OBJECT', 'REAL', 'SMALLINT', 'STRING', 'TEXT', 'TIME', 'TIMESTAMP',
    'TIMESTAMP_LTZ', 'TIMESTAMP_NTZ', 'TIMESTAMP_TZ', 'VARCHAR', 'VARIANT',
]);

/*
 * Ordering matters: comments and strings must win over everything else so a
 * keyword inside a quoted literal is not recoloured.
 *   1 comment  2 string  3 quoted identifier  4 number  5 word  6 operator
 */
const TOKEN_RE = new RegExp([
    /(--[^\n]*|\/\*[\s\S]*?\*\/)/,          // comment
    /('(?:''|\\.|[^'\\])*')/,               // string literal
    /("(?:""|[^"])*"|\[[^\]]*\])/,          // quoted / bracketed identifier
    /(\b\d+(?:\.\d+)?\b)/,                  // number
    /([A-Za-z_][A-Za-z0-9_$]*)/,            // word
    /([(),;.*=<>!+\-/%|:]+)/,               // operator / punctuation
].map(r => r.source).join('|'), 'g');

/** VS Code Dark+ / Light+ derived, so the colours read as "code editor". */
const THEMES = {
    dark: {
        plain: '#d4d4d4',
        keyword: '#569cd6',
        type: '#4ec9b0',
        fn: '#dcdcaa',
        string: '#ce9178',
        number: '#b5cea8',
        comment: '#6a9955',
        operator: '#94a3b8',
        identifier: '#9cdcfe',
    },
    light: {
        plain: '#334155',
        keyword: '#0000ff',
        type: '#267f99',
        fn: '#795e26',
        string: '#a31515',
        number: '#098658',
        comment: '#008000',
        operator: '#64748b',
        identifier: '#001080',
    },
};

export const sqlPalette = (mode) => (mode === 'dark' ? THEMES.dark : THEMES.light);

/**
 * @param {string} code - raw SQL
 * @param {'dark'|'light'} mode - active palette mode
 * @returns {React.ReactNode[]} coloured spans, safe to drop inside a <pre>
 */
export const highlightSql = (code, mode = 'dark') => {
    if (!code) return code;
    const c = sqlPalette(mode);
    const text = String(code);
    const nodes = [];
    let lastIndex = 0;
    let key = 0;

    const push = (value, color, italic) => {
        nodes.push(
            <span key={key++} style={italic ? { color, fontStyle: 'italic' } : { color }}>
                {value}
            </span>
        );
    };

    TOKEN_RE.lastIndex = 0;
    let match;
    while ((match = TOKEN_RE.exec(text)) !== null) {
        const [raw, comment, string, identifier, number, word, operator] = match;

        // Whitespace and anything the grammar does not claim.
        if (match.index > lastIndex) {
            push(text.slice(lastIndex, match.index), c.plain);
        }
        lastIndex = match.index + raw.length;

        if (comment) {
            push(raw, c.comment, true);
        } else if (string) {
            push(raw, c.string);
        } else if (identifier) {
            push(raw, c.identifier);
        } else if (number) {
            push(raw, c.number);
        } else if (word) {
            const upper = raw.toUpperCase();
            if (KEYWORDS.has(upper)) {
                push(raw, c.keyword);
            } else if (TYPES.has(upper)) {
                push(raw, c.type);
            } else if (text[TOKEN_RE.lastIndex] === '(') {
                // A bare word immediately followed by "(" is a call.
                push(raw, c.fn);
            } else {
                push(raw, c.plain);
            }
        } else if (operator) {
            push(raw, c.operator);
        }
    }

    if (lastIndex < text.length) {
        push(text.slice(lastIndex), c.plain);
    }

    return nodes;
};

export default highlightSql;
